import { downloadCsv } from '../../shared/exportData';

/**
 * CSV export for the Search Console tables (spec v2 §7, §10).
 *
 * "Export needs no RPC. You already have every row in the response." — §10. So an export
 * is a pure function of what is currently on screen, and nothing here fetches.
 *
 * ── Raw values, never the rendered ones ─────────────────────────────────────────
 * The table cells are formatted for a human: `3.7%`, `212,840`, `Aug 14`. A CSV lands in
 * a spreadsheet, where a thousands separator makes a number into text and a percent
 * string makes a ratio unsortable. So the export reads the RAW payload field and lets the
 * spreadsheet do the formatting.
 *
 * That is also the only reading of §8 "never calculate a displayed number" that survives
 * contact with an export: converting `0.0372` to `3.72` would be arithmetic we invented,
 * so the ratio ships exactly as the backend computed it and the header says what it is.
 *
 * @param {Array<{header: string, get: (row: object) => unknown}>} columns column spec
 * @param {Array<object>} rows the rows currently on screen
 * @param {string} filename target filename, `.csv` included
 */
export const exportRows = (columns, rows, filename) => {
  const headers = columns.map((c) => c.header);
  const body = (Array.isArray(rows) ? rows : []).map((row) => columns.map((c) => c.get(row)));
  downloadCsv(headers, body, filename);
};

/**
 * Filename for a panel export:
 * `SEARCH CONSOLE WHICH SEARCH LED TO WHICH PAGE 2026-05-18 TO 2026-08-15.csv`.
 *
 * Written to be read in a downloads folder, not parsed. Three things it has to carry:
 * which dashboard it came from, WHICH PANEL — in the words the reader saw on screen —
 * and the window the rows describe.
 *
 * The panel KEY is never used. `query_page_map` and `nearly_there` are database
 * identifiers, underscored and abbreviated, and they tell whoever opens the file in Excel
 * next week nothing about what is in it. The panel's own title is the description.
 *
 * The range is in the name because the same panel exported twice from two different
 * comparison windows produces two files that are otherwise indistinguishable — and mixing
 * those two up in a spreadsheet is exactly the kind of silent population mix the page
 * works hard to avoid on screen.
 *
 * Uppercased so it stands out among everything else in a downloads folder. Only the
 * characters an operating system genuinely refuses are stripped (`/ \ : * ? " < > |`);
 * spaces survive. The extension stays lowercase, which is the convention every tool
 * expects.
 *
 * @param {string} name the panel's title, or its key as a last resort
 * @param {{from?: string|null, to?: string|null}} [range] the window the rows describe
 * @returns {string} the filename
 */
export const csvFilename = (name, range) => {
  const title = String(name || '')
    // A key that slipped through — `query_page_map` — still reads as words.
    .replace(/[_-]+/g, ' ')
    .replace(/[/\\:*?"<>|]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  const parts = ['Search Console', title || 'export'];
  if (range?.from && range?.to) parts.push(`${range.from} to ${range.to}`);
  return `${parts.join(' ').toUpperCase()}.csv`;
};
